import { Button, Combobox, type ComboboxOption } from '@atlas/ui-primitives'
import { useState } from 'react'
import { ChipSet } from '@/components/classification/ChipSet'
import type { Selection } from '@/domain/work-order.types'
import type { RailAxis } from '@/hooks/useLibrary'
import { sortedByLabel } from '@/lib/option-order'

export interface ClassifyPickerProps {
	/** Current classification tuple (empty = nothing attached). */
	value: Selection
	/** Registry axes + values offered by the picker (from `useAxesWithValues`). */
	axes: RailAxis[]
	onChange: (next: Selection) => void
	/** Caption shown when the tuple is empty. */
	emptyLabel?: string
}

/** A code is a plain slug of its label when it matches the label lowercased with
 * non-alphanumerics collapsed to underscores (e.g. `webinar`↔`Webinar`,
 * `clinical_study`↔`Clinical Study`). Coded values (`W1`, `D5`, `bravecto`↔`BRAVECTO®`)
 * differ, so we surface the code as `code · label`. */
const isSlugOf = (code: string, label: string): boolean =>
	code.toLowerCase() ===
	label
		.toLowerCase()
		.replace(/[^a-z0-9]+/gi, '_')
		.replace(/^_+|_+$/g, '')

/** Display a value as `code · label`, or just the label when the code is a plain slug of it. */
export const optionLabel = (code: string, label: string): string =>
	isSlugOf(code, label) ? label : `${code} · ${label}`

const axisOptions = (axis: RailAxis | undefined): ComboboxOption[] =>
	(axis?.options ?? []).map((o) => ({ value: o.code, label: optionLabel(o.code, o.label), depth: o.depth }))

/** Adapt facet-shaped axes (`{ axis, label, values }`) to the `RailAxis` the picker consumes. */
export const facetsToRailAxes = (
	facets: { axis: string; label: string; values: { code: string; label: string }[] }[],
): RailAxis[] =>
	facets.map((f) => ({
		code: f.axis,
		label: f.label,
		options: f.values.map((v) => ({ code: v.code, label: v.label, depth: 0 })),
	}))

/**
 * Generic axis-chip editor for a classification tuple: empty = nothing attached;
 * pick a registry axis (Brand, Geography/market, Species, Channel, …) then a
 * value via searchable Comboboxes (matching the work-order form), and add it as a
 * chip. Reuses `ChipSet` for display/remove. Presentational + storied.
 *
 * Promoted from the rules feature once a second feature (messages) needed it, and
 * rebuilt on `Combobox` + registry `RailAxis` so axis/value options hydrate from
 * the live axis registry (not the facet seed).
 */
export function ClassifyPicker({
	value,
	axes,
	onChange,
	emptyLabel = 'Applies to all — add an axis to narrow it.',
}: ClassifyPickerProps) {
	const [axis, setAxis] = useState<string | undefined>(undefined)
	const [val, setVal] = useState<string | undefined>(undefined)
	const axisLabels = Object.fromEntries(axes.map((a) => [a.code, a.label]))
	const selectedAxis = axes.find((a) => a.code === axis)
	const empty = Object.values(value).every((vs) => vs.length === 0)

	function remove(a: string, v: string) {
		const next = { ...value, [a]: (value[a] ?? []).filter((x) => x !== v) }
		if (next[a].length === 0) delete next[a]
		onChange(next)
	}

	function add() {
		if (!axis || !val) return
		const current = value[axis] ?? []
		if (!current.includes(val)) onChange({ ...value, [axis]: [...current, val] })
		setVal(undefined)
	}

	return (
		<div>
			{empty ? (
				<p className="mb-2 font-mono text-xs text-ds-text-subtle">{emptyLabel}</p>
			) : (
				<div className="mb-2">
					<ChipSet tuple={value} axisLabels={axisLabels} onRemove={remove} />
				</div>
			)}
			<div className="flex flex-wrap items-end gap-2">
				<div className="w-48">
					<Combobox
						aria-label="Axis"
						options={sortedByLabel(axes.map((a) => ({ value: a.code, label: a.label })))}
						value={axis ?? null}
						onChange={(v) => {
							setAxis(v)
							setVal(undefined)
						}}
						placeholder="Add axis…"
					/>
				</div>
				<div className="w-48">
					<Combobox
						aria-label="Value"
						options={axisOptions(selectedAxis)}
						value={val ?? null}
						onChange={setVal}
						placeholder="Value…"
						isDisabled={!axis}
					/>
				</div>
				<Button
					type="button"
					variant="secondary"
					size="sm"
					className="rounded-ds-full"
					isDisabled={!axis || !val}
					onPress={add}
				>
					＋ add axis
				</Button>
			</div>
		</div>
	)
}
